import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { DEEP_GREEN, INK, MUTED, TOP_CUSTOMERS_ACCENT_BG } from '../colorTokens';
import { briefingSlugForAccountIds, slugifyName } from './customerBriefingAccess';
import { hasBriefingAccess } from '../../../services/authService';

// Portal accent orange: used for the thin top bar only (white text on it fails contrast, so buttons stay green)
const ACCENT_ORANGE = '#FF8000';

const updatedLabel = (syncedAt) => {
  const date = syncedAt ? new Date(syncedAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return ` · Updated ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

/**
 * Entry point to the YTD leadership briefing. The whole card is one link to the briefing page inside the portal
 * (opens in a new tab); the page loads its numbers with the portal login and the server sets the date windows.
 * Visibility is gated by Top Customer ACCOUNT_IDs — see customerBriefingAccess.js.
 */
export default function CustomerBriefingSection({
  accountId = null,
  accountIds = null,
  customerName = '',
  syncedAt = null
}) {
  const slug = useMemo(
    () => briefingSlugForAccountIds(accountIds, accountId),
    [accountIds, accountId]
  );

  // The card is only for people granted Briefing access; the server enforces the same rule
  if (!slug || !hasBriefingAccess()) return null;

  // Short link named after the admin account name, e.g. /briefings/clifford-chance-llp
  const href = `/briefings/${slugifyName(customerName) || slug}`;

  return (
    <Box id="customer-briefing" sx={{ width: '100%', minWidth: 0, mb: 2 }}>
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open leadership briefing (opens in a new tab)"
        sx={{
          display: 'block',
          color: 'inherit',
          textDecoration: 'none',
          bgcolor: '#fff',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
          transition: 'box-shadow 150ms ease, transform 150ms ease',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', transform: 'translateY(-1px)' },
          '&:hover .briefing-cta': { bgcolor: '#0F1D15' },
          '&:focus-visible': { outline: `3px solid ${DEEP_GREEN}`, outlineOffset: '2px' },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } }
        }}
      >
        <Box sx={{ height: '4px', bgcolor: ACCENT_ORANGE }} />
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
            <Box
              aria-hidden
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                bgcolor: TOP_CUSTOMERS_ACCENT_BG,
                color: DEEP_GREEN
              }}
            >
              <AssessmentOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ m: 0, fontSize: '1rem', fontWeight: 800, color: INK, letterSpacing: '-0.01em' }}>
                Leadership briefing
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: '0.8125rem', color: INK }}>
                This year vs last year, and before vs after Azure
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: MUTED }}>
                {`Snowflake${updatedLabel(syncedAt)}`}
              </Typography>
            </Box>
          </Box>
          <Box
            className="briefing-cta"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              px: 2,
              py: 1,
              borderRadius: '4px',
              bgcolor: DEEP_GREEN,
              color: '#fff',
              fontSize: '0.8125rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              transition: 'background-color 150ms ease'
            }}
          >
            Open
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
